import { useState } from 'react'

import { Button, Card, Form, Section } from '@factorialco/deprecated-design-system'
import { Form as FactorialForm } from 'factorial-form'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import i18n from 'modules/core/lib/i18n'

import type GDPRDataRetention from 'modules/ats/store/models/GDPRDataRetention'

class CandidatesWithExpiredPermissionForm extends FactorialForm {
  constructor(values: { auto_deletion_enabled: boolean; grace_period_before_deletion: number }) {
    super(values, {
      auto_deletion_enabled: 'boolean',
      grace_period_before_deletion: 'number',
    })
  }
}

interface Props {
  currentRetention: GDPRDataRetention
}

const DataErasure = ({ currentRetention }: Props) => {
  const form = useLazyRef(
    () => new CandidatesWithExpiredPermissionForm(currentRetention.toJS())
  ).current

  const { performAction: saveDataForm } = useAsyncAction(async () => {
    await currentRetention?.save(form.data())
  })

  const { performAction: saveToggleData } = useAsyncAction(async () => {
    await currentRetention?.save({
      auto_deletion_enabled: form.get('auto_deletion_enabled').value,
    })
  })

  const active = !!currentRetention?.attributes.auto_deletion_enabled
  const [confirmationModal, toggleConfirmationModal] = useState(false)

  const toggle = (isExpanded: boolean) => {
    if (isExpanded) {
      toggleConfirmationModal(true)
    } else {
      saveForm(false)
    }
  }

  const saveForm = (value: boolean) => {
    form.setValues({
      auto_deletion_enabled: value,
    })
    saveToggleData()
  }

  return (
    <Section.TwoThirds
      description={i18n.t('app_store_v2.apps.ats.settings.data_erasure.description')}
      illustration="delete"
      title={i18n.t('app_store_v2.apps.ats.settings.data_erasure.title')}
    >
      <Card>
        <Card.Title size="small">
          {i18n.t('ats.settings.data_erasure.candidates_with_expired_permission.title')}
        </Card.Title>
        <Card.Description>
          {i18n.t('ats.settings.data_erasure.candidates_with_expired_permission.description')}
        </Card.Description>
        <Card.ExpandableContent
          expanded={active}
          onExpandChange={toggle}
          disabled={!currentRetention.attributes.consent_limit_enabled}
        >
          <Form onSubmit={saveDataForm} padding={null}>
            <TextField
              label={i18n.t(
                'ats.settings.data_erasure.candidates_with_expired_permission.inputs_label.delete_after'
              )}
              value={form.get('grace_period_before_deletion').value}
              onChange={(value) => form.setValues({ grace_period_before_deletion: Number(value) })}
              indicator={i18n.t(
                'ats.settings.data_erasure.candidates_with_expired_permission.days'
              )}
            />

            {form.isDirty && (
              <Form.Buttons>
                <Button submit>{i18n.t('common.submit')}</Button>
              </Form.Buttons>
            )}
          </Form>
        </Card.ExpandableContent>
      </Card>
      {confirmationModal && (
        <Modal
          title={i18n.t('ats.settings.data_erasure.modal.title')}
          description={i18n.t('ats.settings.data_erasure.modal.description')}
          illustration="alert"
          onClose={() => toggleConfirmationModal(false)}
          onSubmit={() => {
            toggleConfirmationModal(false)
            saveForm(true)
          }}
          footer={
            <Button type="negative" submit>
              {i18n.t('ats.settings.data_erasure.modal.submit_button')}
            </Button>
          }
        />
      )}
    </Section.TwoThirds>
  )
}

export default observer(DataErasure)
